<template>
  <div class="pl-payout-row m-t-sm">
    <div class="flex flex-wrap align-items-center gap-sm">
      <div class="flex-1" style="min-width: 200px;">
        <p class="text-16 font-semi m-b-none">{{ row.first_name }} {{ row.last_name }}</p>
        <p class="text-14 text-gray-600 m-b-none">
          <template v-if="row.last_payout && row.last_payout.amount_cents === 0">
            Starting point set · counting lessons since {{ formatDate(row.last_payout.period_end_at) }}
          </template>
          <template v-else-if="row.last_payout">
            Last paid {{ formatDate(row.last_payout.paid_on) }} ({{ dollars(row.last_payout.amount_cents) }}, through {{ formatDate(row.last_payout.period_end_at) }})
          </template>
          <template v-else>
            Never paid out · counting lessons since {{ formatDate(row.owed_since) }} ·
            <a class="cursor-pointer" :data-cy="`set-starting-point-${row.coach_id}`" @click="startBaseline">paid this coach before?</a>
          </template>
        </p>
      </div>
      <div class="text-right">
        <p class="text-20 font-heavy m-b-none pl-slot-time" :data-cy="`coach-owed-${row.coach_id}`" :class="{ 'text-red-600': row.owed.coach_pay < 0 }">{{ dollars(row.owed.coach_pay) }}</p>
        <p class="text-12 text-gray-600 m-b-none">
          {{ row.owed.lesson_count }} {{ row.owed.lesson_count === 1 ? 'lesson' : 'lessons' }} ·
          {{ dollars(row.owed.revenue) }} revenue<template v-if="row.owed.processing_fees > 0"> &minus; {{ dollars(row.owed.processing_fees) }} processing</template> &minus; {{ dollars(row.owed.facility_cut) }} facility<template v-if="row.owed.cash_collected > 0"> · <span class="text-yellow-700" :data-cy="`cash-collected-${row.coach_id}`">{{ dollars(row.owed.cash_collected) }} collected in cash</span></template><template v-if="row.owed.uncollected_count > 0"> · <span class="text-yellow-700">{{ row.owed.uncollected_count }} not collected</span></template><template v-if="row.owed.refunded_count > 0"> · <span class="text-red-600">{{ row.owed.refunded_count }} refunded</span></template>
        </p>
      </div>
      <button
        v-if="row.owed.coach_pay > 0 && !marking"
        type="button"
        class="btn btn-primary btn-compact flex-shrink-0 pl-mark-paid"
        :data-cy="`mark-paid-${row.coach_id}`"
        @click="startMarkPaid">
        Mark as paid
      </button>
    </div>

    <p v-if="row.owed.coach_pay < 0" class="text-12 text-red-600 m-t-xs m-b-none">
      Refunds exceeded new lesson revenue &mdash; this balance carries forward and offsets what {{ row.first_name }} earns next.
    </p>

    <!-- Inline mark-as-paid form -->
    <div v-if="marking" class="bg-gray-50 rounded-sm p-sm m-t-sm">
      <div class="flex flex-wrap gap-sm align-items-flex-end">
        <div>
          <label :for="`payout-amount-${row.coach_id}`" class="text-12 m-b-none">Amount paid</label>
          <div class="flex align-items-center gap-x-xxs">
            <span class="text-14 text-gray-600">$</span>
            <input :id="`payout-amount-${row.coach_id}`" v-model.number="form.amountDollars" name="amount" type="number" min="0" step="0.01" class="form-control" style="max-width: 120px;">
          </div>
        </div>
        <div>
          <label :for="`payout-date-${row.coach_id}`" class="text-12 m-b-none">Paid on</label>
          <input :id="`payout-date-${row.coach_id}`" v-model="form.paidOn" name="paid_on" type="date" class="form-control">
        </div>
        <div class="flex-1" style="min-width: 160px;">
          <label :for="`payout-notes-${row.coach_id}`" class="text-12 m-b-none">Note <span class="text-gray-500">(optional, e.g. check number)</span></label>
          <input :id="`payout-notes-${row.coach_id}`" v-model="form.notes" name="notes" type="text" class="form-control">
        </div>
      </div>
      <p v-if="row.owed.uncollected_count > 0" class="text-12 text-yellow-700 m-t-xs m-b-none">
        {{ row.owed.uncollected_count }} {{ row.owed.uncollected_count === 1 ? 'lesson' : 'lessons' }} in this window
        {{ row.owed.uncollected_count === 1 ? "hasn't" : "haven't" }} been collected from the parent yet, so that coach pay
        isn't in this total &mdash; it will show up as owed once the parent pays.
      </p>
      <p v-if="row.owed.cash_collected > 0" class="text-12 text-yellow-700 m-t-xs m-b-none">
        {{ dollars(row.owed.cash_collected) }} of this window's revenue was collected in cash or check.
        If {{ row.first_name }} kept that money, reduce the payout so they aren't paid twice.
        <a class="cursor-pointer font-semi" :data-cy="`deduct-cash-${row.coach_id}`" @click="deductCashFromAmount">Deduct it from the amount</a>
      </p>
      <p class="text-12 text-gray-600 m-t-xs m-b-none">
        Covers {{ formatDate(row.owed_since) }} through today. The next payroll run starts from today.
        The lesson breakdown is frozen with the payout, so this record stays accurate even if refunds happen later.
      </p>
      <div class="flex gap-x-xs m-t-sm">
        <ladda-button @lbClicked="savePayout" el-class="btn-primary btn-compact" :loading="saving" data-cy="confirm-payout">
          Record payout
        </ladda-button>
        <button type="button" class="btn btn-default btn-compact" @click="marking = false">Cancel</button>
      </div>
    </div>

    <!-- Starting point: a $0 payout that moves the owed window without recording money,
         for teams that handled payroll outside WrestlingIQ until now -->
    <div v-if="baselining" class="bg-gray-50 rounded-sm p-sm m-t-sm">
      <p class="text-14 m-b-none">
        Already been paying {{ row.first_name }} outside WrestlingIQ? Pick the date this ledger should take over.
        Lessons before that date won't count as owed.
      </p>
      <div class="flex flex-wrap gap-sm align-items-flex-end m-t-xs">
        <div>
          <label :for="`baseline-date-${row.coach_id}`" class="text-12 m-b-none">Count lessons starting on</label>
          <input :id="`baseline-date-${row.coach_id}`" v-model="baselineForm.startDate" name="start_date" type="date" class="form-control">
        </div>
        <div class="flex-1" style="min-width: 160px;">
          <label :for="`baseline-notes-${row.coach_id}`" class="text-12 m-b-none">Note <span class="text-gray-500">(optional)</span></label>
          <input :id="`baseline-notes-${row.coach_id}`" v-model="baselineForm.notes" name="notes" type="text" class="form-control">
        </div>
      </div>
      <div class="flex gap-x-xs m-t-sm">
        <ladda-button @lbClicked="saveBaseline" el-class="btn-primary btn-compact" :loading="saving" data-cy="confirm-baseline">
          Set starting date
        </ladda-button>
        <button type="button" class="btn btn-default btn-compact" @click="baselining = false">Cancel</button>
      </div>
    </div>

    <!-- Lesson detail + payout history (both lazy) -->
    <div class="m-t-xs flex gap-x-sm">
      <a v-if="row.owed.lesson_count > 0" class="text-14 cursor-pointer text-gray-600" :data-cy="`toggle-lessons-${row.coach_id}`" @click="toggleLessons">
        <i :class="lessonsDetail ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>
        {{ lessonsDetail ? 'Hide lessons' : `Show ${row.owed.lesson_count} ${row.owed.lesson_count === 1 ? 'lesson' : 'lessons'}` }}
      </a>
      <a class="text-14 cursor-pointer text-gray-600" @click="toggleHistory">
        {{ showHistory ? 'Hide payout history' : 'Payout history' }}
      </a>
    </div>

    <div v-if="lessonsDetail" class="m-t-xs">
      <p v-if="lessonsDetail.loading" class="text-14 text-gray-500 m-b-none"><i class="fa-solid fa-spinner fa-spin"></i></p>
      <payroll-lesson-table v-else :lessons="lessonsDetail.lessons"></payroll-lesson-table>
    </div>

    <!-- Payout history (lazy) -->
    <div v-if="showHistory" class="m-t-xs">
      <p v-if="historyLoading" class="text-14 text-gray-500 m-b-none"><i class="fa-solid fa-spinner fa-spin"></i></p>
      <p v-else-if="history.length === 0" class="text-14 text-gray-500 m-b-none">No payouts recorded yet.</p>
      <div v-else>
        <div v-for="payout in history" :key="payout.id" class="border-top p-y-xs">
          <div class="flex align-items-center gap-sm">
            <p class="text-14 m-b-none flex-1 pl-slot-time">
              <template v-if="payout.amount_cents === 0">
                {{ formatDate(payout.paid_on) }} · Starting point
                <span class="text-gray-600">&mdash; counting lessons after {{ formatDate(payout.period_end_at) }}</span>
              </template>
              <template v-else>
                {{ formatDate(payout.paid_on) }} · {{ dollars(payout.amount_cents) }}
                <span class="text-gray-600">for lessons through {{ formatDate(payout.period_end_at) }}</span>
                <span v-if="payout.cash_kept_cents > 0" class="text-yellow-700">· kept {{ dollars(payout.cash_kept_cents) }} in cash</span>
              </template>
              <span v-if="payout.notes" class="text-gray-600">· {{ payout.notes }}</span>
            </p>
            <button type="button" class="btn btn-link p-x-xs p-y-xs text-gray-500" :aria-label="`Delete payout from ${formatDate(payout.paid_on)}`" @click="deletePayout(payout)">
              <i class="fa-regular fa-trash-can"></i>
            </button>
          </div>

          <!-- Starting points are markers, not payments — the lessons in
               their window were settled outside WrestlingIQ, so showing
               them here would read as "we paid you for these". -->
          <div v-if="payout.amount_cents > 0" class="flex gap-x-sm m-t-xxs">
            <a class="text-14 cursor-pointer text-gray-600" :data-cy="`toggle-payout-lessons-${payout.id}`" @click="togglePayoutLessons(payout)">
              <i :class="expandedPayouts[payout.id] ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>
              {{ expandedPayouts[payout.id] ? 'Hide lessons' : 'Show lessons' }}
            </a>
            <a class="text-14 cursor-pointer text-gray-600" :data-cy="`download-payout-csv-${payout.id}`" @click="downloadPayoutCsv(payout)">
              <i class="fa-solid fa-download m-r-xxs"></i>CSV
            </a>
          </div>

          <div v-if="expandedPayouts[payout.id]" class="m-t-xs">
            <p v-if="expandedPayouts[payout.id].loading" class="text-14 text-gray-500 m-b-none"><i class="fa-solid fa-spinner fa-spin"></i></p>
            <template v-else>
              <!-- Snapshot payouts are frozen at creation; legacy payouts predate
                   snapshots, so their lesson math is recomputed live and can
                   legitimately drift from the hand-typed amount (rounding, a
                   bonus, a later refund). Label which one the reader is seeing. -->
              <p v-if="payout.snapshot" class="text-12 text-gray-600 m-b-xs">
                Recorded payout {{ dollars(payout.amount_cents) }} · lesson breakdown frozen when the payout was recorded
                {{ formatDate(payout.snapshot.generated_at) }} &mdash; later refunds or edits don't rewrite it.
              </p>
              <p v-else class="text-12 text-gray-600 m-b-xs">
                Recorded payout {{ dollars(payout.amount_cents) }} ·
                lessons in this window today total
                <span class="font-semi">{{ dollars(liveWindowCoachPay(payout)) }}</span>
                <template v-if="liveWindowCoachPay(payout) !== payout.amount_cents">
                  &mdash; a difference here is normal if the payout was rounded or adjusted by hand.
                </template>
              </p>
              <payroll-lesson-table :lessons="expandedPayouts[payout.id].lessons"></payroll-lesson-table>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div v-show="error" class="m-t-xs">
      <small><span class="text-danger">{{ errorMessage }}</span></small>
    </div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import PayrollLessonTable from './payroll_lesson_table.vue';
import { errorableMix } from './mixins/errorable_mix';
import { downloadMix } from './mixins/download_mix';
import { currencyMix } from '../../shared/currency_mix';

// One coach's owed-since-last-payout row on the coach pay page: the owed
// number, mark-as-paid and starting-point forms, lesson drill-down, and payout
// history. Emits 'refresh' whenever a payout is recorded or deleted so the
// parent refetches every coach's window.
export default {
  name: 'payroll-coach-row',
  components: { LaddaButton, PayrollLessonTable },
  mixins: [errorableMix, downloadMix, currencyMix],
  props: {
    row: {
      type: Object,
      required: true,
    },
    // The summary's generation time — payout periods close at this instant so
    // no lesson falls between periods
    generatedAt: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      saving: false,
      marking: false,
      form: {
        amountDollars: 0,
        paidOn: moment().format('YYYY-MM-DD'),
        notes: '',
      },
      baselining: false,
      baselineForm: {
        startDate: moment().format('YYYY-MM-DD'),
        notes: '',
      },
      showHistory: false,
      historyLoading: false,
      history: [],
      // { loading, lessons } for the current owed window, null when collapsed
      lessonsDetail: null,
      // payout_id -> { loading, lessons } for the same detail on a past payout
      expandedPayouts: {},
    };
  },
  watch: {
    // The parent refetched: the owed window moved, so expanded detail is stale
    row() {
      this.lessonsDetail = null;
    },
  },
  methods: {
    startMarkPaid() {
      this.baselining = false;
      this.marking = true;
      this.form = {
        amountDollars: Math.round(this.row.owed.coach_pay) / 100,
        paidOn: moment().format('YYYY-MM-DD'),
        notes: '',
        cashKeptCents: 0,
      };
    },
    // The coach may already be holding this window's cash collections — net
    // them out so the payout only covers what the club actually owes on top
    deductCashFromAmount() {
      const remaining = Math.max(0, this.row.owed.coach_pay - this.row.owed.cash_collected);
      this.form.amountDollars = Math.round(remaining) / 100;
      this.form.cashKeptCents = this.row.owed.cash_collected;
      if (!this.form.notes.includes('kept cash')) {
        this.form.notes = `${this.form.notes} ${this.dollars(this.row.owed.cash_collected)} kept cash collections`.trim();
      }
    },
    savePayout() {
      const vm = this;
      if (vm.saving) {
        return;
      }
      // A cleared amount field would post $0, which the app treats as a
      // starting-point marker rather than a payment
      const amountCents = Math.round((vm.form.amountDollars || 0) * 100);
      if (amountCents <= 0) {
        vm.errorMessage = 'Enter the amount you paid. To move the counting start date without recording money, use "paid this coach before?".';
        vm.error = true;
        return;
      }
      vm.saving = true;
      vm.error = false;

      const params = {
        coach_payout: {
          coach_profile_id: vm.row.coach_id,
          period_start_at: vm.row.owed_since,
          period_end_at: vm.generatedAt,
          amount_cents: amountCents,
          cash_kept_cents: vm.form.cashKeptCents || 0,
          paid_on: vm.form.paidOn,
          notes: vm.form.notes,
        },
      };

      axios.post(vm.$apiService.coachPayoutsUrl(), params)
        .then(() => {
          vm.saving = false;
          vm.marking = false;
          vm.$notificationManager.emit('show-toast', `Payout recorded for ${vm.row.first_name}`, true);
          vm.refreshHistoryIfOpen();
          vm.$emit('refresh');
        })
        .catch((saveError) => {
          vm.saving = false;
          vm.errorMessage = vm.parseErrorResponse(saveError);
          vm.error = true;
        });
    },
    startBaseline() {
      this.marking = false;
      this.baselining = true;
      this.baselineForm = {
        startDate: moment().format('YYYY-MM-DD'),
        notes: 'Earlier lessons paid outside WrestlingIQ',
      };
    },
    // A $0 payout whose period ends on the chosen date — moves the owed
    // starting line without recording money that never went through here
    saveBaseline() {
      const vm = this;
      if (vm.saving) {
        return;
      }
      vm.saving = true;
      vm.error = false;

      // Anchor the period start before the chosen date even when it predates the
      // owed window, so a starting date over a year back doesn't fail validation
      const dayBefore = moment(vm.baselineForm.startDate).subtract(1, 'day');
      const periodStart = moment(vm.row.owed_since).isBefore(dayBefore) ? vm.row.owed_since : dayBefore.toISOString();

      const params = {
        coach_payout: {
          coach_profile_id: vm.row.coach_id,
          period_start_at: periodStart,
          period_end_at: vm.baselineForm.startDate,
          amount_cents: 0,
          paid_on: moment().format('YYYY-MM-DD'),
          notes: vm.baselineForm.notes,
        },
      };

      axios.post(vm.$apiService.coachPayoutsUrl(), params)
        .then(() => {
          vm.saving = false;
          vm.baselining = false;
          vm.$notificationManager.emit('show-toast', `Starting date set for ${vm.row.first_name}`, true);
          vm.refreshHistoryIfOpen();
          vm.$emit('refresh');
        })
        .catch((saveError) => {
          vm.saving = false;
          vm.errorMessage = vm.parseErrorResponse(saveError);
          vm.error = true;
        });
    },
    toggleLessons() {
      const vm = this;
      if (vm.lessonsDetail) {
        vm.lessonsDetail = null;
        return;
      }
      vm.lessonsDetail = { loading: true, lessons: [] };
      // Same window the owed number was computed over
      const url = vm.$apiService.privateLessonPayrollLessonsUrl(vm.row.coach_id, vm.row.owed_since, vm.generatedAt);
      axios.get(url)
        .then((response) => {
          vm.lessonsDetail = { loading: false, lessons: response.data.lessons };
        })
        .catch((fetchError) => {
          vm.lessonsDetail = null;
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    // Snapshot payouts carry their frozen lesson list; legacy payouts recompute
    // the window live — the same query as the current owed window with the
    // payout's bounds, since payouts tile end to end.
    fetchPayoutLessons(payout) {
      const vm = this;
      if (payout.snapshot) {
        vm.$set(vm.expandedPayouts, payout.id, { loading: false, lessons: payout.snapshot.entries });
        return Promise.resolve(payout.snapshot.entries);
      }
      vm.$set(vm.expandedPayouts, payout.id, { loading: true, lessons: [] });
      const url = vm.$apiService.privateLessonPayrollLessonsUrl(vm.row.coach_id, payout.period_start_at, payout.period_end_at);
      return axios.get(url)
        .then((response) => {
          vm.$set(vm.expandedPayouts, payout.id, { loading: false, lessons: response.data.lessons });
          return response.data.lessons;
        })
        .catch((fetchError) => {
          vm.$delete(vm.expandedPayouts, payout.id);
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
          return null;
        });
    },
    togglePayoutLessons(payout) {
      if (this.expandedPayouts[payout.id]) {
        this.$delete(this.expandedPayouts, payout.id);
        return;
      }
      this.fetchPayoutLessons(payout);
    },
    liveWindowCoachPay(payout) {
      const lessons = (this.expandedPayouts[payout.id] || {}).lessons || [];
      return lessons.reduce((sum, lesson) => sum + lesson.coach_pay, 0);
    },
    downloadPayoutCsv(payout) {
      const vm = this;
      const loaded = vm.expandedPayouts[payout.id];
      // Download without expanding first — fetch on demand when they haven't
      const lessons = loaded && !loaded.loading
        ? Promise.resolve(loaded.lessons)
        : vm.fetchPayoutLessons(payout);
      lessons.then((rows) => {
        if (!rows) {
          return;
        }
        vm.downloadCSVFile(
          `payroll-${vm.row.first_name}-${vm.row.last_name}-${moment(payout.paid_on).format('YYYY-MM-DD')}.csv`.toLowerCase(),
          rows.map((lesson) => ({
            'Lesson date': vm.formatDateTime(lesson.lesson_at),
            'Wrestler(s)': lesson.wrestler_names,
            'Duration (min)': lesson.duration_minutes,
            Revenue: vm.csvDollars(lesson.revenue),
            'Processing fee': vm.csvDollars(lesson.processing_fee),
            'Facility fee': vm.csvDollars(lesson.facility_cut),
            'Coach pay': vm.csvDollars(lesson.coach_pay),
            'Parent payment': lesson.payment_status,
          })),
        );
      });
    },
    toggleHistory() {
      if (this.showHistory) {
        this.showHistory = false;
        return;
      }
      this.showHistory = true;
      this.fetchHistory();
    },
    refreshHistoryIfOpen() {
      if (this.showHistory) {
        this.fetchHistory();
      }
    },
    fetchHistory() {
      const vm = this;
      vm.historyLoading = true;
      vm.history = [];
      // Expanded detail is keyed by payout id; a refetch may have dropped one
      vm.expandedPayouts = {};
      vm.$apiService.loadAllPages(vm.$apiService.coachPayoutsUrl(vm.row.coach_id), 'coach_payouts')
        .then((payouts) => {
          vm.history = payouts;
          vm.historyLoading = false;
        })
        .catch((fetchError) => {
          vm.historyLoading = false;
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    deletePayout(payout) {
      const vm = this;
      // Deleting a payout moves the owed window backward for everyone, so a
      // stray tap on the trash icon shouldn't do it silently
      const label = payout.amount_cents === 0 ? 'starting point' : vm.dollars(payout.amount_cents);
      if (!window.confirm(`Delete the ${label} record from ${vm.formatDate(payout.paid_on)}? This changes what ${vm.row.first_name} is owed.`)) {
        return;
      }
      axios.delete(vm.$apiService.coachPayoutUrl(payout.id))
        .then(() => {
          vm.$notificationManager.emit('show-toast', 'Payout deleted', true);
          vm.fetchHistory();
          vm.$emit('refresh');
        })
        .catch((deleteError) => {
          vm.errorMessage = vm.parseErrorResponse(deleteError);
          vm.error = true;
        });
    },
    dollars(cents) {
      return this.formatAsCurrency(cents);
    },
    // Plain decimal so spreadsheets treat the column as a number
    csvDollars(cents) {
      return ((cents || 0) / 100).toFixed(2);
    },
    formatDate(date) {
      return moment(date).format('MMM D, YYYY');
    },
    formatDateTime(date) {
      return moment(date).format('MMM D, YYYY h:mm a');
    },
  },
};
</script>
